import { DownOutlined, HomeOutlined, RightOutlined } from "@ant-design/icons";
import { useState } from "react";
import { Link } from "react-router-dom";
import { DASHBOARD_NAV_STYLE, SHIMMER_STYLE, hexToRgba } from "./SidebarConst";

// const hexToRgba = (hex, alpha) => {
//   if (!hex) return `rgba(255, 255, 255, ${alpha})`;
//   let h = hex.replace("#", "");
//   if (h.length === 3)
//     h = h
//       .split("")
//       .map((c) => c + c)
//       .join("");
//   const r = parseInt(h.slice(0, 2), 16);
//   const g = parseInt(h.slice(2, 4), 16);
//   const b = parseInt(h.slice(4, 6), 16);
//   return `rgba(${isNaN(r) ? 255 : r}, ${isNaN(g) ? 255 : g}, ${isNaN(b) ? 255 : b}, ${alpha})`;
// };

// Returns true if the color is light (needs dark text), false if dark (needs light text)
const isLightColor = (hex) => {
  if (!hex) return false;
  let h = hex.replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  // Perceived brightness (YIQ formula)
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 150;
};

// Darkens (negative percent) or lightens (positive percent) a hex color
const shadeColor = (hex, percent) => {
  if (!hex) return hex;
  let h = hex.replace("#", "");
  if (h.length === 3)
    h = h
      .split("")
      .map((c) => c + c)
      .join("");
  let r = parseInt(h.slice(0, 2), 16);
  let g = parseInt(h.slice(2, 4), 16);
  let b = parseInt(h.slice(4, 6), 16);
  r = Math.min(255, Math.max(0, Math.round(r + (percent / 100) * 255)));
  g = Math.min(255, Math.max(0, Math.round(g + (percent / 100) * 255)));
  b = Math.min(255, Math.max(0, Math.round(b + (percent / 100) * 255)));
  return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
};

const SubTile = ({ item, index, moduleColor, isActive, C }) => {
  const active = isActive(item.path);

  // 👇 THE ONE COLOR YOU CONTROL. Swap moduleColor for any hex you want.
  const bgColor = moduleColor;

  const lightBg = isLightColor(bgColor);
  const borderColor = shadeColor(bgColor, lightBg ? -25 : 20); // darker border on light bg, lighter on dark bg
  const textColor = lightBg ? "#1a1a1a" : "#ffffff";
  const mutedTextColor = lightBg ? "rgba(0,0,0,0.6)" : "rgba(255,255,255,0.65)";

  const shimmerBg = `linear-gradient(
    105deg,
    transparent 25%,
    ${hexToRgba(bgColor, 0.05)} 40%,
    ${hexToRgba(bgColor, 0.12)} 50%,
    ${hexToRgba(bgColor, 0.05)} 60%,
    transparent 75%
  )`;

  return (
    <Link
      to={item.path}
      className="shimmer-item-dynamic"
      style={{
        "--shimmer-bg": shimmerBg,
        "--shimmer-delay": `${(index % 10) * 0.3}s`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "12px 6px 10px",
        borderRadius: 10,
        textDecoration: "none",
        background: active ? bgColor : hexToRgba(bgColor, 0.14),
        // border: `1px solid ${active ? borderColor : #ffffff}`,
        border: `1px solid ${active ? borderColor : "#a1a1a1"}`,
        height: "100%",
        position: "relative",
        boxShadow: active ? `0 4px 14px ${hexToRgba(bgColor, 0.35)}` : "none",
        transition:
          "background 0.18s ease, border-color 0.18s ease, transform 0.22s cubic-bezier(0.4,0,0.2,1), box-shadow 0.22s ease",
      }}
      onMouseEnter={(e) => {
        if (!active) {
          e.currentTarget.style.background = hexToRgba(bgColor, 0.6);
          e.currentTarget.style.borderColor = borderColor;
          e.currentTarget.style.transform = "translateY(-2px)";
          e.currentTarget.style.boxShadow = `0 6px 16px ${hexToRgba(bgColor, 0.25)}`;
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          e.currentTarget.style.background = hexToRgba(bgColor, 0.14);
          e.currentTarget.style.borderColor = "#a1a1a1";
          e.currentTarget.style.transform = "translateY(0)";
          e.currentTarget.style.boxShadow = "none";
        }
      }}
    >
      {active && (
        <span
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 6,
            height: 6,
            borderRadius: "50%",
            background: textColor,
            boxShadow: `0 0 6px ${hexToRgba(textColor, 0.8)}`,
          }}
        />
      )}
      <span
        className="subtile-icon"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: 23,
          height: 23,
          borderRadius: 8,
          fontSize: 13,
          background: active
            ? hexToRgba(textColor, 0.15)
            : hexToRgba(bgColor, 0.4),
          color: active ? C.goldAccent : "#ffffff",
        }}
      >
        {item.icon}
      </span>
      <span
        className="subtile-label"
        style={{
          fontSize: 11,
          fontWeight: active ? 800 : 700,
          color: active ? textColor : "#ffffff",
          fontFamily: "Roboto, sans-serif",
          letterSpacing: "0.04em",
          textAlign: "center",
          lineHeight: 1.25,
          wordBreak: "break-word",
        }}
      >
        {item.label}
      </span>
    </Link>
  );
};

/* ── Accordion group (Reports & Analysis section) ── */
const AccordionGroup = ({
  ag,
  moduleColor,
  isActive,
  openAccordion,
  toggleAccordion,
  C,
}) => {
  const isOpen = openAccordion === ag.key;
  const hasActive = (ag.items || []).some((i) => isActive(i.path));

  const bgActive18 = hexToRgba(moduleColor, 0.18);
  const bgActive28 = hexToRgba(moduleColor, 0.28);
  const bgIdle = "rgba(255,255,255,0.04)";
  const bgIdleHover = "rgba(255,255,255,0.09)";

  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={() => toggleAccordion(ag.key)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "8px 10px",
          background: isOpen || hasActive ? bgActive18 : bgIdle,
          border: `1px solid ${
            isOpen || hasActive
              ? hexToRgba(moduleColor, 0.35)
              : "rgba(255,255,255,0.10)"
          }`,
          borderRadius: 8,
          cursor: "pointer",
          transition:
            "background 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            isOpen || hasActive ? bgActive28 : bgIdleHover;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            isOpen || hasActive ? bgActive18 : bgIdle;
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 7,
            background:
              isOpen || hasActive
                ? `linear-gradient(135deg, ${hexToRgba(moduleColor, 0.95)}, ${shadeColor(moduleColor, -15)})`
                : hexToRgba(moduleColor, 0.18),
            color:
              isOpen || hasActive
                ? isLightColor(moduleColor)
                  ? "#1a1a1a"
                  : "#ffffff"
                : moduleColor,
            fontSize: 15,
            flexShrink: 0,
            boxShadow:
              isOpen || hasActive
                ? `0 2px 8px ${hexToRgba(moduleColor, 0.5)}`
                : "none",
            border: `1px solid ${isOpen || hasActive ? "transparent" : hexToRgba(moduleColor, 0.3)}`,
            transition:
              "background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease",
          }}
        >
          {ag.icon}
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 700,
            color: isOpen || hasActive ? moduleColor : C.text,
            fontFamily: "Roboto, sans-serif",
            textAlign: "left",
          }}
        >
          {ag.label}
        </span>
        <span
          style={{
            fontSize: 11,
            color: isOpen || hasActive ? moduleColor : C.textMuted,
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <DownOutlined />
        </span>
      </button>

      {isOpen && (
        <ul
          style={{
            listStyle: "none",
            margin: "6px 0 0",
            padding: "0 0 0 18px", // 👈 indent so items read as "belonging to" the header
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          {/* 👇 vertical guide line connecting header to its items */}
          <span
            style={{
              position: "absolute",
              left: 7,
              top: 0,
              bottom: 10,
              width: 2,
              borderRadius: 2,
              background: hexToRgba(moduleColor, 0.3),
            }}
          />

          {(ag.items || []).map((item) => {
            const active = isActive(item.path);

            const bgColor = moduleColor;
            const lightBg = isLightColor(bgColor);
            const borderColor = shadeColor(bgColor, lightBg ? -25 : 20);
            const textColor = lightBg ? "#1a1a1a" : "#ffffff";

            return (
              <li key={item.path} style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: -11,
                    top: "50%",
                    width: 10,
                    height: 2,
                    background: hexToRgba(moduleColor, 0.3),
                  }}
                />
                <Link
                  to={item.path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "7px 10px",
                    borderRadius: 7,
                    textDecoration: "none",
                    background: active
                      ? hexToRgba(bgColor, 0.75) // strong fill when active
                      : hexToRgba(bgColor, 0.45), // subtle/muted when inactive
                    border: `1.5px solid ${active ? borderColor : hexToRgba(bgColor, 0.25)}`,
                    boxShadow: active
                      ? `0 0 0 1px ${hexToRgba(bgColor, 0.4)}, 0 4px 12px ${hexToRgba(bgColor, 0.45)}`
                      : "none",
                    position: "relative",
                    transition:
                      "background 0.18s ease, border-color 0.18s ease, transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = active
                      ? hexToRgba(bgColor, 0.85)
                      : hexToRgba(bgColor, 0.32);
                    e.currentTarget.style.transform = "translateX(3px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = active
                      ? hexToRgba(bgColor, 0.75)
                      : hexToRgba(bgColor, 0.45);
                    e.currentTarget.style.transform = "translateX(0)";
                  }}
                >
                  {active && (
                    <span
                      style={{
                        position: "absolute",
                        left: -16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 3,
                        height: 16,
                        borderRadius: "0 3px 3px 0",
                        background: moduleColor,
                        boxShadow: `0 0 6px ${hexToRgba(moduleColor, 0.8)}`,
                      }}
                    />
                  )}
                  {/* <span
          style={{
            width: active ? 7 : 5,
            height: active ? 7 : 5,
            borderRadius: "50%",
            background: active ? textColor : "rgba(255, 255, 255, 0.87)",
            flexShrink: 0,
            boxShadow: active ? `0 0 6px ${hexToRgba(textColor, 0.7)}` : "none",
            transition: "background 0.18s ease, width 0.18s ease, height 0.18s ease",
          }}
        /> */}
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 800 : 500, // 👈 bigger gap: 800 vs 500
                      color: active ? textColor : "rgb(255, 255, 255)", // 👈 dimmer when inactive
                      fontFamily: "Roboto, sans-serif",
                      letterSpacing: active ? "0.02em" : "0",
                      lineHeight: 1.35,
                      transition: "color 0.18s ease",
                    }}
                  >
                    {item.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

/* ── Section heading row ── */
const SectionHeading = ({ label, hasActive, moduleColor, C }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 2px 6px",
      marginTop: 2,
    }}
  >
    <span
      style={{
        width: 3,
        height: 12,
        borderRadius: 2,
        background: hasActive ? C.accent : C.goldAccent,
        flexShrink: 0,
      }}
    />
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        color: "#000",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontFamily: "Roboto, sans-serif",
        borderRadius: 4,
        padding: "2px 6px",
        // background: "#00000000",
        background: `linear-gradient(135deg, #ffe066 0%, #d8b535ee 100%)`,
      }}
    >
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: C.headerBorder }} />
  </div>
);

/* ── Main InventoryScreen component ── */
const InventoryScreen = ({
  group,
  isActive,
  goBack,
  navigate,
  C,
  onReports,
  isReportsScreen,
}) => {
  const [openAccordion, setOpenAccordion] = useState(null);
  const moduleColor = group.color || "#4cdb5f";

  const toggleAccordion = (key) =>
    setOpenAccordion((prev) => (prev === key ? null : key));

  const anyReportActive =
    isReportsScreen ||
    (group.accordionGroups || []).some((ag) =>
      (ag.items || []).some((i) => isActive(i.path)),
    ) ||
    (group.moduleReports || []).some((rep) =>
      (rep.items || []).some((i) => isActive(i.path)),
    );

  return (
    <div
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <style>{SHIMMER_STYLE}</style>
      <style>{DASHBOARD_NAV_STYLE}</style>
      <style>{`
  .scheme-reports-scroll::-webkit-scrollbar {
    width: 4px;
  }
  .scheme-reports-scroll::-webkit-scrollbar-track {
    background: #f5f0ff;
    border-radius: 4px;
  }
  .scheme-reports-scroll::-webkit-scrollbar-thumb {
    background: #e8a4de;
    border-radius: 4px;
  }
`}</style>

      {/* Dashboard strip */}
      <button
        className="dashboard-nav-btn"
        onClick={() => {
          navigate("/dashboard");
          goBack();
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          width: "100%",
          padding: "5px 18px",
          background: isActive("/dashboard")
            ? "#B84A4A" // solid thick active background
            : "#8B3A3A", // solid thick inactive background
          border: "1px solid rgba(255, 255, 255, 0.2)",
          borderRadius: 7,
          cursor: "pointer",
          transition: "background 0.18s ease, transform 0.15s ease",
        }}
        onMouseEnter={(e) => {
          if (!isActive("/dashboard")) {
            e.currentTarget.style.background = "#9C4444";
          }
        }}
        onMouseLeave={(e) => {
          if (!isActive("/dashboard")) {
            e.currentTarget.style.background = "#8B3A3A";
          }
        }}
      >
        <span
          className="dashboard-nav-icon"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 30,
            height: 25,
            borderRadius: 6,
            background: isActive("/dashboard")
              ? "rgba(255, 255, 255, 0.25)" // brighter box for active
              : "rgba(255, 255, 255, 0.15)",
            color: "#FFFFFF", // white icon in both states
            fontSize: 15,
            flexShrink: 0,
          }}
        >
          <HomeOutlined />
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 14,
            fontWeight: 700,
            color: "#FFFFFF", // white text in both states
            fontFamily: "Roboto, sans-serif",
            textAlign: "left",
          }}
        >
          Go To Home
        </span>
        <span
          className="dashboard-nav-arrow"
          style={{
            display: "flex",
            alignItems: "center",
            color: isActive("/dashboard") ? "#FFFFFF" : "#F0D9D9",
            fontSize: 11,
            flexShrink: 0,
          }}
        >
          <RightOutlined />
        </span>
      </button>

      {/* Module title bar */}
      <div
        style={{
          height: 44,
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          gap: 2,
          // padding: "0 10px",
          background: "rgba(0,0,0,0.2)",
          borderTop: "1px solid rgba(255,255,255,0.2)",
          borderBottom: "1px solid rgba(255,255,255,0.2)",
        }}
      >
        <div
          style={{
            width: 140,
            background: moduleColor,
            padding: "5px 3px",
            borderRadius: 6,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <span
            style={{
              fontWeight: 700,
              fontSize: 12,
              color: "#000000",
              fontFamily: "Roboto, sans-serif",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
              textAlign: "center",
            }}
          >
            {group.label}
          </span>
        </div>
        <button
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 6,
            border: "1px solid rgba(255,255,255,0.25)",
            background: moduleColor,
            cursor: "pointer",
            flexShrink: 0,
            color: "#000000",
            fontSize: 11,
            marginLeft: "5px",
            fontWeight: 600,
          }}
        >
          {group.count}
        </button>
      </div>

      {/* Scrollable content */}
      <div
        className="scheme-reports-scroll"
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "thin",
          scrollbarColor: "rgba(255,255,255,0.2) transparent",
          padding: "8px 6px 8px",
          minHeight: 0,
          background: "rgba(0,0,0,0.15)",
        }}
      >
        {/* Sub-groups (icon tiles) */}
        {(group.subGroups || []).map((sg) => {
          const hasActive = (sg.items || []).some((i) => isActive(i.path));
          const singleCol =
            ((group.subGroups || []).length === 1 &&
              (sg.items || []).length <= 2) ||
            (sg.items || []).length === 1;

          return (
            <div key={sg.key} style={{ marginBottom: 12 }}>
              <SectionHeading
                label={sg.label}
                hasActive={hasActive}
                moduleColor={moduleColor}
                C={C}
              />
              <div
                style={
                  singleCol
                    ? {
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 4,
                      }
                    : {
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 4,
                      }
                }
              >
                {(sg.items || []).map((item, index) => (
                  <div
                    key={item.path}
                    style={{ width: singleCol ? "50%" : "auto" }}
                  >
                    <SubTile
                      item={item}
                      index={index}
                      moduleColor={moduleColor}
                      isActive={isActive}
                      C={C}
                    />
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Accordion groups (Reports & Analysis) */}
        {(group.accordionGroups || []).length > 0 && (
          <>
            <SectionHeading
              label={group.accordianTitle || "Reports & Analysis"}
              hasActive={anyReportActive}
              moduleColor={moduleColor}
              C={C}
            />
            {group.accordionGroups.map((ag) => (
              <AccordionGroup
                key={ag.key}
                ag={ag}
                moduleColor={moduleColor}
                isActive={isActive}
                openAccordion={openAccordion}
                toggleAccordion={toggleAccordion}
                C={C}
              />
            ))}
          </>
        )}
      </div>

      {/* Footer: Reports button + copyright */}
      <div
        style={{
          flexShrink: 0,
          borderTop: "1px solid rgba(255,255,255,0.18)",
          background: "rgba(0,0,0,0.3)",
          padding: "6px 8px",
          display: "flex",
          flexDirection: "column",
          gap: 8,
        }}
      >
        <button
          onClick={onReports}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            width: "100%",
            padding: "9px 12px",
            borderRadius: 8,
            border: `1px solid ${C.accent}`,
            cursor: "pointer",
            background: anyReportActive ? C.reportsBg : C.accordionBodyBg,
            boxShadow: anyReportActive
              ? `0 0 18px ${moduleColor}40, inset 0 1px 0 rgba(255,255,255,0.06)`
              : "inset 0 1px 0 rgba(255,255,255,0.04)",
            transition: "all 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = anyReportActive
              ? C.accordionBgHover
              : C.bgHover;
            e.currentTarget.style.boxShadow = anyReportActive
              ? `0 0 24px ${moduleColor}59, inset 0 1px 0 rgba(255,255,255,0.08)`
              : "0 0 12px rgba(255,255,255,0.06)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = anyReportActive
              ? C.reportsBg
              : C.accordionBodyBg;
            e.currentTarget.style.boxShadow = anyReportActive
              ? `0 0 18px ${moduleColor}40, inset 0 1px 0 rgba(255,255,255,0.06)`
              : "inset 0 1px 0 rgba(255,255,255,0.04)";
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 24,
              height: 24,
              borderRadius: 6,
              background: anyReportActive
                ? `${moduleColor}2e`
                : "rgba(255,255,255,0.08)",
              color: anyReportActive ? moduleColor : "rgba(255,255,255,0.65)",
              fontSize: 12,
              flexShrink: 0,
            }}
          >
            📊
          </span>
          <span
            style={{
              fontSize: 12,
              fontWeight: 700,
              color: C.accent,
              fontFamily: "Roboto, sans-serif",
              letterSpacing: "0.02em",
            }}
          >
            Reports
          </span>
          {anyReportActive && (
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: moduleColor,
                flexShrink: 0,
                boxShadow: `0 0 8px ${moduleColor}`,
              }}
            />
          )}
        </button>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            color: C.textActive,
            fontSize: 9,
            fontFamily: "Roboto, sans-serif",
          }}
        >
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 11,
              height: 11,
              border: `1px solid ${C.textActive}`,
              borderRadius: "50%",
              fontSize: 7,
              fontWeight: 700,
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            C
          </span>
          <span>Timesera Software Solutions Pvt Ltd</span>
        </div>
      </div>
    </div>
  );
};

export default InventoryScreen;
